/**
 * Task #3065 — Web hardening.
 *
 * Helpers for the HttpOnly-cookie refresh-token proxy hosted at
 * `/api/auth/proxy/*` on the API server. Used by AuthProvider and
 * AuthCallbackPage to move the Supabase refresh token off
 * localStorage and into an HttpOnly cookie owned by the API server.
 *
 * Flow:
 *   1. SPA calls Supabase directly (password / OAuth / OTP / MFA) and
 *      receives a `{ access_token, refresh_token }` session.
 *   2. SPA POSTs the refresh_token to `/api/auth/proxy/adopt`. The
 *      API server stashes it in an HttpOnly `bac_rt` cookie and
 *      returns a CSRF token (also set as a non-HttpOnly companion
 *      `bac_csrf` cookie).
 *   3. SPA calls `supabase.auth.setSession` with the access token and
 *      an inert placeholder (auth-js requires a non-empty refresh token).
 *      The real refresh token is cookie-only; persistSession is disabled.
 *   4. When the access token nears expiry, SPA calls
 *      `/api/auth/proxy/refresh` (CSRF-protected) to get a fresh
 *      access token. The cookie is rotated server-side.
 */

const BASE = (import.meta.env.BASE_URL as string | undefined) || "/";

const CSRF_COOKIE = "bac_csrf";

export const PROXY_REFRESH_PLACEHOLDER = "proxy-managed-session";

export type AdoptResult = { csrf_token: string };
export type ProxyAccessToken = {
  access_token: string;
  expires_in: number;
  csrf_token?: string;
};

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = name + "=";
  for (const part of document.cookie.split(";")) {
    const s = part.trim();
    if (s.startsWith(prefix)) {
      try {
        return decodeURIComponent(s.slice(prefix.length));
      } catch {
        return s.slice(prefix.length);
      }
    }
  }
  return null;
}

/**
 * Hand a fresh Supabase refresh_token to the proxy so it can move
 * into an HttpOnly cookie. Returns the matching CSRF token, which
 * the proxy also set as a non-HttpOnly cookie — callers should
 * remember it in memory to echo back on /refresh + /logout.
 */
export async function adoptRefreshToken(refreshToken: string): Promise<AdoptResult> {
  const res = await fetch(`${BASE}api/auth/proxy/adopt`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      // Custom header required by the proxy to prevent simple-form
      // CSRF. Browsers will trigger a CORS preflight for this header
      // from any non-same-origin caller, which our CORS config
      // rejects for hostile origins.
      "X-Requested-With": "bookaclassic",
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) {
    throw new Error(`adopt failed (${res.status})`);
  }
  return res.json();
}

/**
 * Probe whether the HttpOnly refresh cookie exists. Cheap GET that
 * does NOT verify the token — use this to decide whether to attempt
 * a hydrating /refresh on first paint.
 */
export async function probeSession(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}api/auth/proxy/session`, {
      method: "GET",
      credentials: "same-origin",
    });
    if (!res.ok) return false;
    const j = await res.json();
    return !!j.authenticated;
  } catch {
    return false;
  }
}

/**
 * Exchange the HttpOnly refresh cookie for a fresh access token.
 * Requires the CSRF double-submit pair (cookie + header) — the SPA
 * remembers the CSRF token in memory and we also fall back to
 * reading the companion cookie.
 */
export async function refreshAccessToken(csrfTokenInMemory: string | null): Promise<ProxyAccessToken | null> {
  const csrf = csrfTokenInMemory || readCookie(CSRF_COOKIE);
  if (!csrf) return null;
  const res = await fetch(`${BASE}api/auth/proxy/refresh`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
    },
  });
  if (!res.ok) return null;
  return res.json();
}

/**
 * Clear the HttpOnly + CSRF cookies. CSRF-protected so a hostile
 * site cannot drive-by sign the user out.
 */
export async function proxyLogout(csrfTokenInMemory: string | null): Promise<void> {
  const csrf = csrfTokenInMemory || readCookie(CSRF_COOKIE);
  if (!csrf) return;
  try {
    await fetch(`${BASE}api/auth/proxy/logout`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrf },
    });
  } catch {
    // best-effort — local state is already cleared by the caller
  }
}

/**
 * Strict counterpart for flows that must not continue after a failed remote
 * sign-out. Generic sign-out remains best-effort; intentional account
 * switching needs the caller to observe network and HTTP failures.
 */
export async function proxyLogoutStrict(csrfTokenInMemory: string | null): Promise<void> {
  const csrf = csrfTokenInMemory || readCookie(CSRF_COOKIE);
  if (!csrf) throw new Error("Missing CSRF token for proxy sign-out");
  const res = await fetch(`${BASE}api/auth/proxy/logout`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrf },
  });
  if (!res.ok) {
    throw new Error(`Proxy sign-out failed (${res.status})`);
  }
}
